import React, { useState } from 'react';
import { UnscheduledEvent } from '../../types';
import { EVENT_STATUS } from '../../constants';

/**
 * Props for {@link EventModalFooter}.
 */
interface EventModalFooterProps {
  mode: 'view' | 'edit' | 'create';
  formData: UnscheduledEvent;
  /**
   * Treat the record as if it is being completed even when its live status is
   * not COMPLETED (e.g. during a Move, which closes the source as COMPLETED).
   * Surfaces the Summary button and the Audit required-state styling so the
   * completion prerequisites are visible before the move.
   */
  treatAsCompleted?: boolean;
  summaryRequired: boolean;
  summaryCompleted: boolean;
  summarizing?: boolean;
  auditCompleted: boolean;
  saveDisabled: boolean;
  /** Conditions still blocking the Save/Update button, shown on hover. */
  saveDisabledReasons?: string[];
  onClose: () => void;
  onSummary: () => void;
  onSave: () => void;
  onOpenAudit: () => void;
  onOpenTechNotes: () => void;
}

/**
 * Renders the event modal's footer action buttons (Cancel/Close, Summary,
 * Save/Create/Update, Audit, Tech Notes) with conditional visibility and styling.
 */
const EventModalFooter: React.FC<EventModalFooterProps> = ({
  mode,
  formData,
  treatAsCompleted = false,
  summaryRequired,
  summaryCompleted,
  summarizing = false,
  auditCompleted,
  saveDisabled,
  saveDisabledReasons = [],
  onClose,
  onSummary,
  onSave,
  onOpenAudit,
  onOpenTechNotes,
}) => {
  const isEditOrCreate = mode === 'edit' || mode === 'create';
  const isCompleted = formData.status === EVENT_STATUS.COMPLETED || treatAsCompleted;
  const showSummary = isEditOrCreate && (summaryRequired || isCompleted);
  const auditStateClass = isCompleted ? (auditCompleted ? 'audit-blue' : 'audit-red') : '';
  const [showBlockers, setShowBlockers] = useState<boolean>(false);
  const blockersVisible = saveDisabled && showBlockers && saveDisabledReasons.length > 0;

  return (
    <div className="modal-footer">
      <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
        {mode === 'view' ? 'Close' : 'Cancel'}
      </button>
      {showSummary && (
        <button
          className={`modal-btn modal-btn-summary ${summaryCompleted ? 'summary-blue' : 'summary-red'}`}
          type="button"
          onClick={onSummary}
          disabled={summarizing}
        >
          {summarizing ? 'Summarizing…' : 'Summary'}
        </button>
      )}
      {isEditOrCreate && (
        <div
          className="save-btn-wrap"
          onMouseEnter={() => setShowBlockers(true)}
          onMouseLeave={() => setShowBlockers(false)}
        >
          <button
            className="modal-btn modal-btn-save"
            type="button"
            onClick={onSave}
            disabled={saveDisabled}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </button>
          {blockersVisible && (
            <div className="save-blockers-popup" role="tooltip">
              <div className="save-blockers-title">
                Still required to {mode === 'create' ? 'create' : 'update'}:
              </div>
              <ul className="save-blockers-list">
                {saveDisabledReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {mode === 'edit' && (
        <>
          <button
            className={`modal-btn modal-btn-audit ${auditStateClass}`}
            type="button"
            onClick={onOpenAudit}
          >
            Audit
          </button>
          <button className="modal-btn" type="button" onClick={onOpenTechNotes} title="Tech Notes">
            Tech Notes
          </button>
        </>
      )}
    </div>
  );
};

export default EventModalFooter;
